import {
  getCountries,
  getCountryCallingCode,
  type CountryCode,
} from 'libphonenumber-js';

export type CountryOption = {
  iso2: string;
  name: string;
  dialCode: string;
};

let cachedCountries: CountryOption[] | null = null;

function displayName(iso2: string, locale = 'en'): string {
  try {
    return new Intl.DisplayNames([locale], { type: 'region' }).of(iso2) ?? iso2;
  } catch {
    return iso2;
  }
}

/** ISO countries with English names and dial codes for intake form selects. */
export function getCountryOptions(): CountryOption[] {
  if (cachedCountries) return cachedCountries;

  const options: CountryOption[] = [];
  for (const iso2 of getCountries()) {
    try {
      options.push({
        iso2,
        name: displayName(iso2),
        dialCode: `+${getCountryCallingCode(iso2 as CountryCode)}`,
      });
    } catch {
      // skip unsupported codes
    }
  }
  options.sort((a, b) => a.name.localeCompare(b.name));
  cachedCountries = options;
  return options;
}

export function findCountryByIso(
  iso2: string | null | undefined
): CountryOption | null {
  if (!iso2) return null;
  const upper = iso2.toUpperCase();
  return getCountryOptions().find(c => c.iso2 === upper) ?? null;
}

/** Names that are not a substring of the English country label. */
const COUNTRY_ALIASES: Record<string, string> = {
  uk: 'GB',
  usa: 'US',
  uae: 'AE',
};

export type FilterCountriesOptions = {
  /** When false, dial digits and calling codes are ignored (residence picker). Default true. */
  matchDialCode?: boolean;
};

function matchesCountryQuery(
  country: CountryOption,
  query: string,
  matchDialCode: boolean
): boolean {
  // Aliases are exclusive so `uk` is GB only (Ukraine also starts with "uk").
  const aliasIso = COUNTRY_ALIASES[query];
  if (aliasIso) return country.iso2 === aliasIso;

  const dial = country.dialCode.replace('+', '');
  const digits = query.replace('+', '');
  // Two characters or fewer must not substring-match names.
  // Prefix-match ISO, English name, and (optionally) numeric dials.
  if (query.length <= 2) {
    if (country.iso2.toLowerCase().startsWith(query)) return true;
    if (country.name.toLowerCase().startsWith(query)) return true;
    return matchDialCode && /^\d+$/.test(digits) && dial.startsWith(digits);
  }
  if (
    country.name.toLowerCase().includes(query) ||
    country.iso2.toLowerCase().includes(query)
  ) {
    return true;
  }
  if (!matchDialCode) return false;
  return country.dialCode.includes(query) || dial.includes(digits);
}

export function filterCountries(
  query: string,
  options: FilterCountriesOptions = {}
): CountryOption[] {
  const matchDialCode = options.matchDialCode !== false;
  const q = query.trim().toLowerCase();
  if (!q) return getCountryOptions();
  return getCountryOptions().filter(country =>
    matchesCountryQuery(country, q, matchDialCode)
  );
}
